/**
 * NjmmisWatchdogStrip — 7-day green/red pip strip for the Admin tab.
 * ─────────────────────────────────────────────────────────────────────────────
 * iter-134 — Surfaces the last 7 nights of NJMMIS DDE landing-page
 * watchdog probes (iter-132) as a compact visual so drift is one glance
 * away, no need to hit the /njmmis/watchdog/history endpoint or dig
 * through incidents_ai_watch.
 *
 * Pip semantics:
 *   • 🟢 green   — probe ran, no drift detected, http 200
 *   • 🔴 red     — probe ran, drift detected (structural hash changed)
 *   • ⚠️ amber   — probe ran but fetch failed (http != 200 or exception)
 *   • ⚪ gray    — no probe on that night (scheduler hadn't landed yet /
 *                  server was down / iter-132 hadn't shipped)
 *
 * Data source: GET /api/njmmis/watchdog/history?limit=7 (admin only,
 * added in iter-132 alongside the on-demand probe endpoint).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, RefreshCw, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { apiCall } from '../utils/api';

const pipToneFor = (snap) => {
  if (!snap) return { bg: 'bg-slate-300', ring: 'ring-slate-200', label: 'No probe' };
  if (snap.fetch_error) return { bg: 'bg-amber-400', ring: 'ring-amber-200', label: 'Fetch failed' };
  if (snap.http_status && snap.http_status !== 200) {
    return { bg: 'bg-amber-400', ring: 'ring-amber-200', label: `HTTP ${snap.http_status}` };
  }
  if (snap.drift) return { bg: 'bg-red-500', ring: 'ring-red-200', label: 'Drift detected' };
  return { bg: 'bg-emerald-500', ring: 'ring-emerald-200', label: 'No drift' };
};

const fmtCapturedAt = (iso) => {
  if (!iso) return '—';
  return String(iso).slice(0, 19).replace('T', ' ');
};

/**
 * Build a 7-slot array indexed by the ISO date the pip represents,
 * newest on the right. If a night is missing from the history payload
 * we render a gray "no probe" pip instead of shifting neighbors, so
 * the temporal axis stays honest.
 */
const build7DaySlots = (rows) => {
  const byDate = new Map();
  for (const r of rows || []) {
    const key = (r.captured_at || '').slice(0, 10);
    if (key) byDate.set(key, r);
  }
  const slots = [];
  const today = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    slots.push({ date: key, snap: byDate.get(key) || null });
  }
  return slots;
};

const NjmmisWatchdogStrip = () => {
  const [rows, setRows] = useState(null); // null = initial load
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [probing, setProbing] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall('/njmmis/watchdog/history?limit=7', 'GET');
      const list = res?.data?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || 'Network error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const runProbeNow = useCallback(async () => {
    if (probing) return;
    setProbing(true);
    try {
      await apiCall('/njmmis/watchdog/probe', 'POST');
      await fetchHistory();
    } catch (e) {
      setError(e?.message || 'Probe failed');
    } finally {
      setProbing(false);
    }
  }, [probing, fetchHistory]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const slots = build7DaySlots(rows);
  // Header status — worst-severity slot dominates so the card summary
  // matches the strip.
  const anyDrift = slots.some((s) => s.snap && s.snap.drift);
  const anyFetchErr = slots.some((s) => s.snap && (s.snap.fetch_error || (s.snap.http_status && s.snap.http_status !== 200)));
  const anyClean = slots.some((s) => s.snap && !s.snap.drift && !s.snap.fetch_error && s.snap.http_status === 200);
  let HeaderIcon = ShieldQuestion;
  let headerTone = 'text-slate-500';
  let headerText = 'No probe history yet — click Probe Now to seed the baseline.';
  if (anyDrift) {
    HeaderIcon = ShieldAlert;
    headerTone = 'text-red-600';
    headerText = 'NJMMIS DDE landing markup drift detected in the last 7 nights.';
  } else if (anyFetchErr && !anyClean) {
    HeaderIcon = ShieldAlert;
    headerTone = 'text-amber-600';
    headerText = 'NJMMIS landing probes are failing to fetch — verify origin.';
  } else if (anyClean) {
    HeaderIcon = ShieldCheck;
    headerTone = 'text-emerald-600';
    headerText = 'No drift detected — DDE landing markup stable for the last 7 nights.';
  }

  return (
    <Card data-testid="njmmis-watchdog-strip" className="border-slate-200">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <HeaderIcon className={`w-4 h-4 mt-0.5 ${headerTone}`} />
          <div>
            <CardTitle className="text-sm font-semibold text-slate-700">NJMMIS Watchdog (7d)</CardTitle>
            <p className={`text-[11px] mt-0.5 ${headerTone}`} data-testid="njmmis-watchdog-strip-headline">
              {headerText}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={runProbeNow}
            disabled={probing || loading}
            className="text-[10px] px-2 py-1 rounded-md border border-slate-300 hover:border-slate-400 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
            data-testid="njmmis-watchdog-probe-btn"
            title="Trigger a probe now"
          >
            {probing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Probe Now'}
          </button>
          <button
            onClick={fetchHistory}
            disabled={loading}
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
            title="Refresh history"
            data-testid="njmmis-watchdog-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </CardHeader>
      <CardContent className="pt-1">
        <div className="flex items-end gap-2" data-testid="njmmis-watchdog-strip-pips">
          {slots.map((slot) => {
            const tone = pipToneFor(slot.snap);
            return (
              <div key={slot.date} className="flex flex-col items-center gap-1 flex-1 min-w-0">
                <div
                  className={`w-4 h-4 rounded-full ${tone.bg} ring-2 ${tone.ring} shadow-sm`}
                  title={`${slot.date} — ${tone.label}${slot.snap ? ` · ${fmtCapturedAt(slot.snap.captured_at)}` : ''}`}
                  data-testid={`njmmis-watchdog-pip-${slot.date}`}
                />
                <span className="text-[9px] text-slate-500 tabular-nums">{slot.date.slice(5)}</span>
              </div>
            );
          })}
        </div>
        {error && (
          <div className="mt-2 text-[10px] text-red-600">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NjmmisWatchdogStrip;
